import * as React from 'react';
import Grid from '@mui/material/Grid2';
import {
  Box,
  CircularProgress,
  Collapse,
  IconButton,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from '@mui/material';
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import BaseButton from '../../../components/BaseButton';
import BaseDateRangePicker, {
  INVALID_DATE_RANGE,
} from '../../../components/BaseDateRangePicker';
import BaseInput from '../../../components/BaseInput';
import DataStore from '../../../store';
import { validateForm } from '../../../helpers/formValidator';
import { notificationStore } from '../../../store/Notification';
import { MISSING_FIELDS_MESSAGE } from '../../../helpers/constants';
import { observer } from 'mobx-react';
import { DateTime } from 'luxon';

type SampleRow = {
  sampleId: string;
  sampleStatus: string;
  sampleType: string;
  deliveryDataPackageLink: string;
  receivedDate: string;
  reportDate: string;
};

type SlideRow = {
  slideId: string;
  well: string;
  labelledDna: string;
  fSegmentedMbp: number | null;
  classifiedMbp: number | null;
  processDateSeg: string;
  processDateCls: string;
};

type SampleOrder = {
  customer: string;
  creationDate: string;
  orderNumber: string;
  sampleType: string;
  samples: SampleRow[];
  status: string;
};

const CLASSIFIED_MBP_LOW = 30;
const CLASSIFIED_MBP_MEDIUM = 75;
const MANY_WELLS = 5;

const getClassifiedMbpSumStyle = (sum: number, wells: number) => {
  const manyWells = wells >= MANY_WELLS;
  if (sum < CLASSIFIED_MBP_LOW) {
    return manyWells
      ? { backgroundColor: '#ef9a9a', color: '#b71c1c' }
      : { backgroundColor: '#ffcdd2', color: '#b71c1c' };
  }
  if (sum < CLASSIFIED_MBP_MEDIUM) {
    return manyWells
      ? { backgroundColor: '#ffcc80', color: '#e65100' }
      : { backgroundColor: '#fff59d', color: '#f57f17' };
  }
  return manyWells
    ? { backgroundColor: '#dcedc8', color: '#33691e' }
    : { backgroundColor: '#a5d6a7', color: '#1b5e20' };
};

// Nested table fills the parent row's width and scrolls on its own instead
// of widening the parent table's columns.
// Left padding indents the nested table to show the hierarchy.
const NESTED_TABLE_SX = {
  width: 0,
  minWidth: '100%',
  boxSizing: 'border-box',
  overflowX: 'auto',
  pl: 4,
};

const getSampleStatusStyle = (status: string) => {
  switch (status.toLowerCase()) {
    case 'published':
      return { backgroundColor: '#c8e6c9', color: '#1b5e20' };
    case 'rejected':
      return { backgroundColor: '#ffcdd2', color: '#b71c1c' };
    case 'low coverage':
      return { backgroundColor: '#b3e5fc', color: '#01579b' };
    default:
      return { backgroundColor: '#e0e0e0', color: '#333333' };
  }
};

const IFRAME_DOWNLOAD_DELAY_MS = 500;
const IFRAME_REMOVE_DELAY_MS = 60000;

// Signed CloudFront links send no CORS headers, so fetch() cannot read them.
// A hidden iframe per link lets the browser download each zip without
// opening a tab; the delay keeps browsers from dropping rapid downloads.
const downloadDeliveryPackages = async (samples: SampleRow[]) => {
  for (const sample of samples) {
    const frame = document.createElement('iframe');
    frame.style.display = 'none';
    frame.src = sample.deliveryDataPackageLink;
    document.body.appendChild(frame);
    setTimeout(() => frame.remove(), IFRAME_REMOVE_DELAY_MS);
    await new Promise((resolve) =>
      setTimeout(resolve, IFRAME_DOWNLOAD_DELAY_MS)
    );
  }
};

// Metrics DB was searched but has no measurement for the well
const renderMetricCell = (value: number | string | null) =>
  value === null || value === '' ? (
    <TableCell sx={{ color: 'text.disabled' }}>no data</TableCell>
  ) : (
    <TableCell>{value}</TableCell>
  );

const SampleTrackingRow = ({ sample }: { sample: SampleRow }) => {
  const [open, setOpen] = React.useState(false);
  const [slides, setSlides] = React.useState<SlideRow[] | null>(null);
  const [loading, setLoading] = React.useState(false);

  const classifiedMbpValues = (slides || [])
    .map((slide) => slide.classifiedMbp)
    .filter((value): value is number => value !== null);
  const classifiedMbpSum = classifiedMbpValues.length
    ? Math.round(classifiedMbpValues.reduce((a, b) => a + b, 0) * 100) / 100
    : null;

  const toggleOpen = async () => {
    const nextOpen = !open;
    setOpen(nextOpen);
    if (!nextOpen || slides !== null || loading) {
      return;
    }

    setLoading(true);
    const resData = await DataStore.sampleManagerTrackingSlidesRequest(
      sample.sampleId
    );
    setLoading(false);
    if (resData?.messageType === 'error' || !resData?.response) {
      return;
    }
    setSlides(resData.response.slides || []);
  };

  const renderSlides = () => {
    if (loading) {
      return <CircularProgress size={20} sx={{ my: 1 }} />;
    }
    if (!slides || slides.length === 0) {
      return (
        <Typography variant='body2' sx={{ my: 1 }}>
          {slides ? 'No slides to display.' : 'Slides could not be loaded.'}
        </Typography>
      );
    }
    return (
      <Table size='small' aria-label='slides'>
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 700 }}>Well ID</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Labelled DNA</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>F. Segmented Mbp</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Classified Mbp</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Process Date SEG</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Process Date CLS</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {slides.map((slide) => (
            <TableRow key={`${slide.slideId}-${slide.well}`}>
              <TableCell>{`${slide.slideId}${slide.well}`}</TableCell>
              <TableCell>{slide.labelledDna}</TableCell>
              {renderMetricCell(slide.fSegmentedMbp)}
              {renderMetricCell(slide.classifiedMbp)}
              {renderMetricCell(slide.processDateSeg)}
              {renderMetricCell(slide.processDateCls)}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  };

  return (
    <>
      <TableRow>
        <TableCell sx={{ padding: '2px 4px', width: '32px' }}>
          <IconButton size='small' onClick={toggleOpen} sx={{ padding: '2px' }}>
            {open ? (
              <KeyboardArrowUp fontSize='small' />
            ) : (
              <KeyboardArrowDown fontSize='small' />
            )}
          </IconButton>
        </TableCell>
        <TableCell>{sample.sampleId}</TableCell>
        <TableCell>{sample.sampleType}</TableCell>
        <TableCell>
          {sample.sampleStatus && (
            <Box
              component='span'
              sx={{
                borderRadius: 1,
                px: 1,
                py: 0.25,
                display: 'inline-block',
                fontSize: '0.875rem',
                whiteSpace: 'nowrap',
                ...getSampleStatusStyle(sample.sampleStatus),
              }}
            >
              {sample.sampleStatus}
            </Box>
          )}
        </TableCell>
        <TableCell>{slides?.length}</TableCell>
        <TableCell>
          {slides && classifiedMbpSum !== null && (
            <Box
              component='span'
              sx={{
                borderRadius: 1,
                px: 1,
                py: 0.25,
                display: 'inline-block',
                fontSize: '0.875rem',
                ...getClassifiedMbpSumStyle(classifiedMbpSum, slides.length),
              }}
            >
              {classifiedMbpSum}
            </Box>
          )}
        </TableCell>
        <TableCell>{sample.receivedDate}</TableCell>
        <TableCell>{sample.reportDate}</TableCell>
        <TableCell>
          {sample.deliveryDataPackageLink ? (
            <Link
              href={sample.deliveryDataPackageLink}
              target='_blank'
              rel='noopener noreferrer'
            >
              Link
            </Link>
          ) : (
            ''
          )}
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell sx={{ padding: 0 }} colSpan={9}>
          <Collapse in={open} timeout='auto' unmountOnExit>
            <Box sx={NESTED_TABLE_SX}>{renderSlides()}</Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

type SampleSortKey = 'sampleId' | 'sampleStatus' | 'receivedDate';

const OrderRow = ({ order }: { order: SampleOrder }) => {
  const [open, setOpen] = React.useState(false);
  const [downloading, setDownloading] = React.useState(false);
  const [sortKey, setSortKey] = React.useState<SampleSortKey | null>(null);
  const [sortDirection, setSortDirection] = React.useState<'asc' | 'desc'>(
    'asc'
  );
  const orderLabel = order.orderNumber || '(no order number)';
  const samplesWithLink = order.samples.filter(
    (sample) => sample.deliveryDataPackageLink
  );

  const onDownloadAll = async () => {
    setDownloading(true);
    await downloadDeliveryPackages(samplesWithLink);
    setDownloading(false);
  };

  const onSort = (key: SampleSortKey) => {
    setSortDirection(
      sortKey === key && sortDirection === 'asc' ? 'desc' : 'asc'
    );
    setSortKey(key);
  };

  const sortedSamples = React.useMemo(() => {
    if (!sortKey) {
      return order.samples;
    }
    const direction = sortDirection === 'asc' ? 1 : -1;
    return [...order.samples].sort((a, b) => {
      const aValue = a[sortKey] || '';
      const bValue = b[sortKey] || '';
      // Keep samples without a value at the bottom in both directions.
      if (!aValue || !bValue) {
        return Number(!aValue) - Number(!bValue);
      }
      return (
        direction * aValue.localeCompare(bValue, undefined, { numeric: true })
      );
    });
  }, [order.samples, sortKey, sortDirection]);

  const renderSortableHeader = (key: SampleSortKey, label: string) => (
    <TableCell
      sx={{ fontWeight: 700 }}
      sortDirection={sortKey === key ? sortDirection : false}
    >
      <TableSortLabel
        active={sortKey === key}
        direction={sortKey === key ? sortDirection : 'asc'}
        onClick={() => onSort(key)}
      >
        {label}
      </TableSortLabel>
    </TableCell>
  );
  const getStatusStyle = (status: string) => {
    if (status === 'Due') {
      return { backgroundColor: '#e0e0e0', color: '#333333' };
    }
    if (status === 'Done') {
      return { backgroundColor: '#c8e6c9', color: '#1b5e20' };
    }
    return { backgroundColor: '#bbdefb', color: '#0d47a1' };
  };

  return (
    <>
      <TableRow
        hover
        sx={{
          height: '32px',
          '&:last-of-type td': {
            borderBottom: 'none',
          },
        }}
      >
        <TableCell sx={{ padding: '2px 4px', width: '32px' }}>
          <IconButton
            size='small'
            onClick={() => setOpen((prev) => !prev)}
            sx={{ padding: '2px' }}
          >
            {open ? (
              <KeyboardArrowUp fontSize='small' />
            ) : (
              <KeyboardArrowDown fontSize='small' />
            )}
          </IconButton>
        </TableCell>
        <TableCell sx={{ padding: '2px 8px' }}>{order.customer}</TableCell>
        <TableCell sx={{ padding: '2px 8px' }}>
          <Box
            onClick={() => setOpen((prev) => !prev)}
            sx={{ cursor: 'pointer', width: '100%' }}
          >
            {orderLabel}
          </Box>
        </TableCell>
        <TableCell sx={{ padding: '2px 8px' }}>{order.creationDate}</TableCell>
        <TableCell
          sx={{
            padding: '2px 8px',
            maxWidth: 140,
            whiteSpace: 'normal',
            wordBreak: 'break-word',
          }}
        >
          {order.sampleType}
        </TableCell>
        <TableCell sx={{ padding: '2px 8px' }}>
          {order.samples.length}
        </TableCell>
        <TableCell sx={{ padding: '2px 8px' }}>
          <Box
            component='span'
            sx={{
              borderRadius: 1,
              px: 1,
              py: 0.25,
              display: 'inline-block',
              fontSize: '0.875rem',
              ...getStatusStyle(order.status),
            }}
          >
            {order.status}
          </Box>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell sx={{ padding: 0 }} colSpan={7}>
          <Collapse in={open} timeout='auto' unmountOnExit>
            <Box sx={NESTED_TABLE_SX}>
              <Table size='small' aria-label='children'>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ padding: '2px 4px', width: '32px' }} />
                    {renderSortableHeader('sampleId', 'Sample ID')}
                    <TableCell sx={{ fontWeight: 700 }}>Sample type</TableCell>
                    {renderSortableHeader('sampleStatus', 'Sample status')}
                    <TableCell sx={{ fontWeight: 700 }}>N. wells</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>
                      Classified Mbp Sum
                    </TableCell>
                    {renderSortableHeader('receivedDate', 'Received')}
                    <TableCell sx={{ fontWeight: 700 }}>Report</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>
                      Delivery package
                      {samplesWithLink.length > 1 && (
                        <Link
                          component='button'
                          variant='body2'
                          onClick={onDownloadAll}
                          disabled={downloading}
                          sx={{ ml: 1, fontWeight: 400 }}
                        >
                          {downloading
                            ? 'Downloading…'
                            : `Download all (${samplesWithLink.length})`}
                        </Link>
                      )}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedSamples.map((sample) => (
                    <SampleTrackingRow
                      key={`${order.orderNumber}-${sample.sampleId}`}
                      sample={sample}
                    />
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

const Tracking = observer(() => {
  const [errors, setErrors] = React.useState({});
  const [orders, setOrders] = React.useState<SampleOrder[]>([]);
  const [statusSortDirection, setStatusSortDirection] = React.useState<
    'asc' | 'desc' | null
  >(null);
  const DATE_RANGE_LAST_DAY = 'last day';
  const DATE_RANGE_LAST_WEEK = 'last week';
  const DATE_RANGE_LAST_MONTH = 'last month';
  const DATE_RANGE_NO_LIMIT = 'clear';
  const [data, setData] = React.useState({
    erp_customer: '',
    dateRange: null as string | null,
  });

  const fieldLabels: { [key: string]: string } = {
    erp_customer: 'Customer ID',
    dateRange: 'Date Range',
  };

  const validationSchema = {
    erp_customer: { type: 'string', required: false },
  };

  const onChangeData = (event: any) => {
    const { name, value } = event.target;
    setData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const showOrders = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();

    if (data.dateRange === INVALID_DATE_RANGE) {
      notificationStore.showMessage('Some fields are invalid.', 'error', {
        'Date Range': 'Date Range is not in a valid format.',
      });
      return;
    }

    const payload = {
      ...data,
      dateRange: data.dateRange || null,
    };

    const validError = validateForm(payload, validationSchema);
    if (validError) {
      const humanizedErrors = Object.entries(validError).reduce(
        (acc: any, [key, message]) => {
          acc[fieldLabels[key] || key] = message;
          return acc;
        },
        {}
      );
      notificationStore.showMessage(
        MISSING_FIELDS_MESSAGE,
        'error',
        humanizedErrors
      );
      setErrors(humanizedErrors);
      return;
    }

    setErrors({});
    const resData = await DataStore.sampleManagerTrackingRequest(payload);
    const responseOrders = resData?.response?.orders || [];
    setOrders(responseOrders);
  };

  const onSortByStatus = () => {
    setStatusSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
  };

  const sortedOrders = React.useMemo(() => {
    if (!statusSortDirection) {
      return orders;
    }
    const direction = statusSortDirection === 'asc' ? 1 : -1;
    return [...orders].sort((a, b) => {
      // Keep orders without a status at the bottom in both directions.
      if (!a.status || !b.status) {
        return Number(!a.status) - Number(!b.status);
      }
      return direction * a.status.localeCompare(b.status);
    });
  }, [orders, statusSortDirection]);

  const getDateRangeValues = (type: string, number: number) => {
    const now = DateTime.now().startOf('day');
    const from = now.minus({ [type]: number }).toFormat('yyyyLLdd');
    const to = now.toFormat('yyyyLLdd');

    return `${from}-${to}`;
  };

  const getPresetDateRange = (option: string) => {
    switch (option) {
      case DATE_RANGE_LAST_DAY:
        return getDateRangeValues('days', 1);
      case DATE_RANGE_LAST_WEEK:
        return getDateRangeValues('weeks', 1);
      case DATE_RANGE_LAST_MONTH:
        return getDateRangeValues('months', 1);
      default:
        return '';
    }
  };

  const getSelectDateOptions = () => {
    const options = [
      { label: 'day', value: DATE_RANGE_LAST_DAY },
      { label: 'week', value: DATE_RANGE_LAST_WEEK },
      { label: 'month', value: DATE_RANGE_LAST_MONTH },
      { label: 'clear', value: DATE_RANGE_NO_LIMIT },
    ];

    return (
      <Box display='flex' alignItems='center' justifyContent='flex-end'>
        <Typography variant='body2' sx={{ mr: 0.5 }}>
          last:
        </Typography>
        {options.map((option, index) => (
          <Box key={option.label} display='flex' alignItems='center'>
            <Link
              component='button'
              variant='body2'
              onClick={() =>
                setData((prev) => ({
                  ...prev,
                  dateRange:
                    option.value === DATE_RANGE_NO_LIMIT
                      ? ''
                      : getPresetDateRange(option.value),
                }))
              }
              sx={{
                fontWeight: 600,
                textTransform: 'lowercase',
                mx: index < options.length - 1 ? 0.5 : 0,
              }}
            >
              {option.label}
            </Link>
            {index < options.length - 1 && (
              <Typography variant='body2' sx={{ mx: 0.5 }}>
                |
              </Typography>
            )}
          </Box>
        ))}
      </Box>
    );
  };

  return (
    <>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 5 }}>
          <BaseInput
            label={fieldLabels.erp_customer}
            value={data.erp_customer}
            name='erp_customer'
            onChange={onChangeData}
            errors={errors}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 7 }}>
          <BaseDateRangePicker
            value={data.dateRange ?? ''}
            name='dateRange'
            onChange={onChangeData}
            errors={errors}
            description={getSelectDateOptions()}
          />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <BaseButton
            text='Show'
            variant='contained'
            color='primary'
            isDisabled={DataStore.isProcessing}
            spinner={DataStore.isProcessing}
            onClick={showOrders}
          />
        </Grid>
      </Grid>

      {orders.length > 0 ? (
        <TableContainer component={Paper} sx={{ mt: 3, boxShadow: 0 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ padding: '0px 4px', width: '32px' }} />
                <TableCell>Customer</TableCell>
                <TableCell>Order number</TableCell>
                <TableCell>Creation date</TableCell>
                <TableCell>Sample type</TableCell>
                <TableCell>Samples</TableCell>
                <TableCell sortDirection={statusSortDirection ?? false}>
                  <TableSortLabel
                    active={statusSortDirection !== null}
                    direction={statusSortDirection ?? 'asc'}
                    onClick={onSortByStatus}
                  >
                    Status
                  </TableSortLabel>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedOrders.map((order, index) => (
                <OrderRow key={`${order.orderNumber}-${index}`} order={order} />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Typography variant='body2' sx={{ mt: 3 }}>
          No orders to display.
        </Typography>
      )}
    </>
  );
});

export default Tracking;
